import { Link } from 'react-router-dom';
import { Code, Headphones, Info, QrCode, ShoppingCart } from 'lucide-react';
import { formatINR } from '../../../utils/format';
import { Rating } from '../../../ui';

const scrollToReviews = () => {
  const target = document.getElementById('reviews');
  if (!target) return;
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
  target.focus({ preventScroll: true });
};

// Average and count from the fetched reviews.
const RatingSummary = ({ reviews }) => {
  if (!reviews || reviews.status === 'loading') {
    return <span className="pk-skel pd-buy__rating-skel" aria-hidden="true" />;
  }
  if (reviews.status === 'error') return null;
  if (reviews.count === 0) {
    return <p className="pd-buy__rating pd-buy__rating--none">No reviews yet</p>;
  }

  const label = `Rated ${reviews.average.toFixed(1)} out of 5 from ${reviews.count} ${
    reviews.count === 1 ? 'review' : 'reviews'
  }. Go to reviews.`;

  return (
    <button
      type="button"
      className="pd-buy__rating pd-stars-read"
      onClick={scrollToReviews}
      aria-label={label}
    >
      <Rating disabled allowHalf size="small" value={reviews.average} />
      <span className="pd-buy__rating-avg">{reviews.average.toFixed(1)}</span>
      <span className="pd-buy__rating-count">
        ({reviews.count} {reviews.count === 1 ? 'review' : 'reviews'})
      </span>
    </button>
  );
};

/** Right-hand buy card: what it is, what it costs, and how buying works. */
const ProductBuyCard = ({ product, description, reviews }) => (
  <div className="pk-card pk-sticky pd-buy">
    {product.category && (
      <Link
        className="pd-buy__category"
        to={`/products?category=${encodeURIComponent(product.category)}`}
      >
        {product.category}
      </Link>
    )}
    <h1 className="pd-buy__title">{product.name}</h1>
    <RatingSummary reviews={reviews} />

    {description && <p className="pd-buy__desc">{description}</p>}

    <p className="pd-buy__price">
      <span className="sr-only">Price: </span>
      {formatINR(product.price, { free: true })}
    </p>

    {/* Signed-out buyers still go to checkout; its route guard sends them to sign in and back. */}
    <Link
      className="pk-btn pk-btn--primary pk-btn--block pd-buy__cta"
      to={`/checkout/${product.id}`}
    >
      <ShoppingCart aria-hidden="true" />
      Buy now
    </Link>

    <ul className="pd-facts">
      <li>
        <Code aria-hidden="true" />
        <span>Full source code</span>
      </li>
      <li>
        <QrCode aria-hidden="true" />
        <span>Pay by UPI — verified within 12 hours</span>
      </li>
      <li>
        <Info aria-hidden="true" />
        <span>
          <Link className="pk-link" to="/refund">
            All sales are final
          </Link>
        </span>
      </li>
      <li>
        <Headphones aria-hidden="true" />
        <span>Support Mon–Fri</span>
      </li>
    </ul>

    <p className="pd-buy__seller">Sold by Projects Kart</p>
  </div>
);

export default ProductBuyCard;
